import React, { useState, Suspense } from 'react';
import { FileText, CheckCircle, LayoutTemplate, Download, Settings, Eye, Loader2 } from 'lucide-react';
import { CVData } from './components/CVEditor';
import { FAQSection } from './components/FAQSection';

// Lazy load heavy components
const CVContent = React.lazy(() => import('./components/CVContent').then(module => ({ default: module.CVContent })));
const CVEditor = React.lazy(() => import('./components/CVEditor').then(module => ({ default: module.CVEditor })));

function App() {
  const [isAtsMode, setIsAtsMode] = useState(false);
  const [region, setRegion] = useState<'EU' | 'CA' | 'GULF'>('EU');
  const [template, setTemplate] = useState<'modern' | 'minimalist' | 'classic' | 'tech' | 'executive' | 'academic'>('modern');
  const defaultCVData: CVData = {
    name: 'Alexandre Dupont',
    title: 'Ingénieur Logiciel Senior',
    email: 'alexandre.dupont@example.com',
    phone: '+33 6 12 34 56 78',
    location: 'Paris, France',
    linkedin: 'alexandre-dupont',
    themeColor: '#0ea5e9',
    // New fields for Gulf region
    nationality: 'Française',
    maritalStatus: 'Célibataire',
    dateOfBirth: '1995-05-15',
    skills: [
      'JavaScript', 'TypeScript', 'React', 'Node.js', 'Python', 'Java',
      'AWS', 'Docker', 'Kubernetes', 'CI/CD', 'Git', 'Agile/Scrum',
      'SQL', 'NoSQL', 'GraphQL', 'REST APIs'
    ],
    languages: [
      { name: 'Français', level: 100, label: 'Natif' },
      { name: 'Anglais', level: 90, label: 'Courant' },
      { name: 'Espagnol', level: 60, label: 'Intermédiaire' }
    ],
    profile: 'Ingénieur logiciel expérimenté avec plus de 5 ans d\'expérience dans le développement d\'applications web scalables. Passionné par les technologies modernes et les meilleures pratiques de développement. Expert en React et Node.js, avec une solide compréhension des architectures cloud et microservices.',
    experiences: [
      {
        title: 'Senior Full Stack Developer',
        company: 'Tech Solutions Inc.',
        location: 'Paris, France',
        period: '01/2022 – Présent',
        points: [
          'Leadership technique d\'une équipe de 5 développeurs',
          'Conception et implémentation d\'une nouvelle architecture microservices',
          'Amélioration des performances de l\'application de 40%',
          'Mise en place de pipelines CI/CD automatisés'
        ],
        tech: ['React', 'Node.js', 'AWS', 'Docker', 'TypeScript']
      },
      {
        title: 'Développeur Web',
        company: 'Digital Agency',
        location: 'Lyon, France',
        period: '06/2019 – 12/2021',
        points: [
          'Développement de sites e-commerce pour des clients majeurs',
          'Intégration de designs responsives et accessibles',
          'Collaboration étroite avec les équipes design et produit'
        ],
        tech: ['Vue.js', 'PHP', 'Laravel', 'MySQL', 'Sass']
      },
      {
        title: 'Stage Ingénieur',
        company: 'StartUp Innovative',
        location: 'Bordeaux, France',
        period: '01/2019 – 06/2019',
        points: [
          'Participation au développement d\'un MVP',
          'Correction de bugs et ajout de nouvelles fonctionnalités',
          'Rédaction de documentation technique'
        ],
        tech: ['JavaScript', 'React', 'Firebase']
      }
    ],
    education: [
      {
        degree: 'Master en Informatique',
        institution: 'École d\'Ingénieurs de Paris',
        period: '2017 – 2019'
      },
      {
        degree: 'Licence Informatique',
        institution: 'Université de Lyon',
        period: '2014 – 2017'
      }
    ],
    photoUrl: ''
  };

  const [cvData, setCvData] = useState<CVData>(() => {
    const savedData = localStorage.getItem('cvData');
    return savedData ? JSON.parse(savedData) : defaultCVData;
  });

  React.useEffect(() => {
    localStorage.setItem('cvData', JSON.stringify(cvData));
  }, [cvData]);

  const handlePrint = () => {
    window.print();
  };

  const LoadingFallback = () => (
    <div className="flex items-center justify-center h-64 text-slate-400">
      <Loader2 className="w-8 h-8 animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-100 font-sans text-slate-900">
      {/* Navbar */}
      <nav className="bg-slate-900 text-white p-4 sticky top-0 z-50 shadow-lg print:hidden">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-2">
            <img src="/logs.png" alt="Logo" className="w-8 h-8 object-contain" />
            <h1 className="text-xl font-bold tracking-tight">CV Generator <span className="text-sky-400">Pro</span></h1>
          </div>
          <div className="flex gap-4">
            <button
              onClick={() => setIsAtsMode(!isAtsMode)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md transition-all font-medium ${isAtsMode
                ? 'bg-indigo-600 text-white shadow-indigo-500/30 shadow-lg ring-2 ring-indigo-400'
                : 'bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-white'
                }`}
            >
              {isAtsMode ? (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Mode ATS Activé
                </>
              ) : (
                <>
                  <LayoutTemplate className="w-4 h-4" />
                  Mode Standard
                </>
              )}
            </button>
            <button
              onClick={handlePrint}
              className="flex items-center gap-2 bg-sky-600 hover:bg-sky-500 text-white px-4 py-2 rounded-md font-medium transition-colors shadow-lg shadow-sky-900/20"
            >
              <Download className="w-4 h-4" />
              Imprimer / PDF
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto p-8 print:p-0">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 print:block">
          {/* Editor Section */}
          <div className="print:hidden">
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden sticky top-24">
              <div className="p-4 border-b border-slate-100 bg-slate-50/50">
                <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                  <Settings className="w-5 h-5 text-slate-500" />
                  Éditeur
                </h2>
              </div>
              <Suspense fallback={<LoadingFallback />}>
                <CVEditor
                  data={cvData}
                  onChange={setCvData}
                  region={region}
                  onRegionChange={setRegion}
                  template={template}
                  onTemplateChange={setTemplate}
                />
              </Suspense>
            </div>
          </div>

          {/* Preview Section */}
          <div className="print:w-full">
            <div className="bg-slate-800 rounded-t-xl p-3 flex justify-between items-center text-white print:hidden">
              <span className="font-medium flex items-center gap-2">
                <Eye className="w-4 h-4 text-slate-400" />
                Aperçu
              </span>
              <span className="text-xs text-slate-400 bg-slate-900/50 px-2 py-1 rounded">
                {isAtsMode ? 'Format ATS Optimisé' : 'Format Standard A4'}
              </span>
            </div>
            <div className="border-x border-b border-slate-200 bg-slate-200/50 p-8 print:p-0 print:border-none print:bg-white overflow-auto max-h-[calc(100vh-12rem)] print:max-h-none custom-scrollbar">
              <div className="origin-top transform print:transform-none transition-transform duration-200 ease-out">
                <Suspense fallback={<LoadingFallback />}>
                  <CVContent
                    data={cvData}
                    isAtsMode={isAtsMode}
                    region={region}
                    template={template}
                  />
                </Suspense>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <FAQSection />
    </div>
  );
}

export default App;